import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Package, ShoppingBag, MessageSquare, LogOut, X, Wrench } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

const EMPTY = {
  name: "", category: "air-compressors", brand: "Indoforce", price: "", mrp: "",
  stock: 0, sku: "", short_description: "", description: "", images: "", specsText: "", featured: false, active: true,
};

const CATS = [
  ["air-compressors", "Air Compressors"], ["compressor-spares", "Compressor Spares"],
  ["car-wash-machines", "Car Wash Machines"], ["car-wash-spares", "Car Wash Spares"], ["accessories", "Accessories"],
];

export default function AdminDashboard() {
  const { user, authLoading, logout } = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/admin"); return; }
    if (!user.is_admin) { navigate("/admin"); return; }
    setReady(true);
  }, [user, authLoading, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [p, o, e] = await Promise.all([
        api.get("/admin/products"), api.get("/admin/orders"), api.get("/admin/enquiries"),
      ]);
      setProducts(p.data); setOrders(o.data); setEnquiries(e.data);
    } catch {
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { if (ready) loadAll(); }, [ready, loadAll]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY, ...p,
      price: p.price, mrp: p.mrp || "", images: (p.images || []).join(", "),
      specsText: Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price are required");
    setSaving(true);
    const specs = {};
    form.specsText.split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) specs[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    });
    const payload = {
      name: form.name, category: form.category, brand: form.brand,
      price: parseFloat(form.price), mrp: form.mrp ? parseFloat(form.mrp) : null,
      stock: parseInt(form.stock) || 0, sku: form.sku || null,
      short_description: form.short_description, description: form.description,
      specs, images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
      featured: !!form.featured, active: !!form.active,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setShowForm(false);
      loadAll();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const setOrderStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, null, { params: { status } });
    toast.success("Order updated");
    loadAll();
  };

  if (authLoading || !ready) {
    return <div className="min-h-screen grid place-items-center bg-[#0A0A0A]"><div className="w-10 h-10 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const TABS = [
    { k: "products", label: "Products", icon: Package, n: products.length },
    { k: "orders", label: "Orders", icon: ShoppingBag, n: orders.length },
    { k: "enquiries", label: "Enquiries", icon: MessageSquare, n: enquiries.length },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain">
      <header className="relative z-10 border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 bg-[#E31E24] text-black"><Wrench size={20} strokeWidth={2.5} /></span>
            <div>
              <p className="font-display font-black uppercase tracking-tighter leading-none">Admin Console</p>
              <p className="text-white/40 text-[11px]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-white/60 hover:text-[#E31E24] text-xs uppercase tracking-widest">View store</button>
            <button onClick={async () => { await logout(); navigate("/admin"); }} data-testid="logout-btn" className="flex items-center gap-2 border border-white/20 hover:border-[#E31E24] px-4 py-2 text-xs uppercase tracking-widest">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-2 mb-8 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`flex items-center gap-2 px-5 py-4 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === t.k ? "border-[#E31E24] text-[#E31E24]" : "border-transparent text-white/50 hover:text-white"}`}
            >
              <t.icon size={15} /> {t.label} <span className="text-white/30">({t.n})</span>
            </button>
          ))}
        </div>

        {tab === "products" && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Products</h2>
              <button onClick={openNew} data-testid="add-product-btn" className="btn-fill flex items-center gap-2 border border-[#E31E24] text-[#E31E24] hover:text-black px-5 py-3 text-xs uppercase tracking-widest font-bold">
                <Plus size={16} /> Add product
              </button>
            </div>
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-white/40 uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="text-left p-4">Product</th><th className="text-left p-4">Category</th>
                    <th className="text-left p-4">Price</th><th className="text-left p-4">Stock</th>
                    <th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {products.map((p) => (
                    <tr key={p.id} data-testid={`admin-product-${p.id}`} className="hover:bg-white/[0.02]">
                      <td className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#161616] overflow-hidden shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                        <span className="font-medium">{p.name}</span>
                      </td>
                      <td className="p-4 text-white/50">{p.category}</td>
                      <td className="p-4 font-display font-bold">{formatINR(p.price)}</td>
                      <td className="p-4">{p.stock}</td>
                      <td className="p-4">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${p.active ? "text-green-400" : "text-white/40"}`}>{p.active ? "Active" : "Hidden"}{p.featured ? " · ★" : ""}</span>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 text-white/60 hover:text-[#E31E24]"><Pencil size={16} /></button>
                        <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="p-2 text-white/60 hover:text-[#E63946]"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "orders" && (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-white/40 uppercase text-[10px] tracking-widest">
                <tr><th className="text-left p-4">Order</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Amount</th><th className="text-left p-4">Payment</th><th className="text-left p-4">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {orders.length === 0 ? <tr><td colSpan={5} className="p-10 text-center text-white/40">No orders yet.</td></tr> :
                  orders.map((o) => (
                    <tr key={o.id} data-testid={`admin-order-${o.id}`}>
                      <td className="p-4 font-mono text-xs">{o.id}</td>
                      <td className="p-4">{o.customer_name}<br /><span className="text-white/40 text-xs">{o.customer_phone}</span></td>
                      <td className="p-4 font-display font-bold">{formatINR(o.amount)}</td>
                      <td className="p-4"><span className={o.payment_status === "paid" ? "text-green-400" : "text-white/50"}>{o.payment_status}</span></td>
                      <td className="p-4">
                        <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} className="bg-transparent border border-white/20 px-2 py-1 text-xs">
                          {["pending", "confirmed", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s} className="bg-[#111]">{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="grid md:grid-cols-2 gap-4">
            {enquiries.length === 0 ? <p className="text-white/40 p-10">No enquiries yet.</p> :
              enquiries.map((e) => (
                <div key={e.id} className="border border-white/10 p-5" data-testid={`admin-enquiry-${e.id}`}>
                  <div className="flex justify-between">
                    <p className="font-display font-bold">{e.name}</p>
                    <span className="text-white/30 text-xs font-mono">{e.id}</span>
                  </div>
                  <p className="text-white/50 text-sm">{e.email} · {e.phone}</p>
                  {e.product && <p className="text-[#E31E24] text-xs mt-1 uppercase tracking-wider">Re: {e.product}</p>}
                  <p className="text-white/70 text-sm mt-3">{e.message}</p>
                </div>
              ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4 overflow-auto" data-testid="product-form-modal">
          <div className="bg-[#111111] border border-white/10 w-full max-w-2xl my-8">
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <h3 className="font-display text-xl font-bold uppercase tracking-tight">{editing ? "Edit product" : "Add product"}</h3>
              <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white"><X /></button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-auto">
              <Field label="Name"><input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                    {CATS.map(([v, l]) => <option key={v} value={v} className="bg-[#111]">{l}</option>)}
                  </select>
                </Field>
                <Field label="Brand"><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} /></Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Price (₹)"><input data-testid="form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} /></Field>
                <Field label="MRP (₹)"><input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className={inputCls} /></Field>
                <Field label="Stock"><input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} /></Field>
              </div>
              <Field label="Short description"><input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className={inputCls} /></Field>
              <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls + " resize-none"} /></Field>
              <Field label="Image URLs (comma separated)"><input data-testid="form-images" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className={inputCls} placeholder="https://..." /></Field>
              <Field label="Specs (one per line, Key: Value)"><textarea rows={4} value={form.specsText} onChange={(e) => setForm({ ...form, specsText: e.target.value })} className={inputCls + " resize-none font-mono text-xs"} placeholder={"Motor: 2 HP\nTank: 50 L"} /></Field>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="border border-white/20 px-6 py-3 text-xs uppercase tracking-widest">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="save-product-btn" className="btn-fill border border-[#E31E24] text-[#E31E24] hover:text-black px-6 py-3 text-xs uppercase tracking-widest font-bold disabled:opacity-50">{saving ? "Saving..." : "Save product"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-transparent border border-white/20 focus:border-[#E31E24] outline-none px-4 py-3 text-sm";
const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-2">{label}</label>
    {children}
  </div>
);
